import { useCallback, useRef, useState } from 'react';
import { useScreenSize } from '../../../utils/utils';
import { PopoverPosition, PopoverProps } from '../types';
import { useRepositioning } from './useRepositioning';
import { useOutsideEvent } from './useOutsideEvent';
import { useButtonClick } from './useButtonClick';
import { useApplyPopoverPosition } from './useApplyPopoverPosition';
import { useHandleOpen } from './useHandleOpen';
import { useCalculatePosition } from './useCalculatePosition';

export function usePopover({
  position = 'top',
  activeHover = false,
  modalOnMediumScreen = false,
}: Pick<PopoverProps, 'position' | 'activeHover' | 'modalOnMediumScreen'>) {
  // Refs for DOM elements
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // State management
  const [isOpen, setIsOpen] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [currentPosition, setCurrentPosition] =
    useState<PopoverPosition>(position);

  // Detect mobile devices
  const screenSize = useScreenSize();
  const isMobile = modalOnMediumScreen
    ? ['xs', 'sm', 'md'].includes(screenSize)
    : ['xs', 'sm'].includes(screenSize);

  // Determine if it's a tooltip on desktop
  const isDesktopTooltip = activeHover && !isMobile;

  // Calculate optimal position before displaying the popover
  const calculatePosition = useCalculatePosition({
    triggerRef,
    contentRef,
    currentPosition,
    position,
  });

  // Apply position to the popover wrapper
  const applyPopoverPosition = useApplyPopoverPosition({
    triggerRef,
    contentWrapperRef,
    contentRef,
    currentPosition,
  });

  // Handle opening and closing the popover
  const handleOpen = useHandleOpen({
    isMobile,
    activeHover,
    isOpen,
    calculatePosition,
    setCurrentPosition,
    setIsOpen,
    setIsHover,
  });

  // Handle click on the trigger button
  const handleButtonClick = useButtonClick({
    isMobile,
    activeHover,
    isOpen,
    handleOpen,
    setCurrentPosition,
    calculatePosition,
  });

  // Close the popover
  const closePopup = useCallback(() => {
    handleOpen(false);
  }, [handleOpen]);

  // Handle outside clicks
  useOutsideEvent({
    ref: contentRef,
    isOpen,
    onOutsideClick: closePopup,
  });

  // Effect to reposition the popover on scroll and resize
  useRepositioning({
    isOpen,
    isMobile,
    contentWrapperRef,
    applyPopoverPosition,
  });

  return {
    // Refs
    triggerRef,
    contentWrapperRef,
    contentRef,

    // State
    isOpen,
    isHover,
    isMobile,
    isDesktopTooltip,
    currentPosition,

    // Event handlers
    handleOpen,
    handleButtonClick,
    closePopup,
  };
}
